import React, { useState } from 'react';
import { Input, Slider, Select, Button, Upload, Checkbox, Tabs } from 'antd';
import { UploadOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { CanvasObject } from '../types';
import { ColorInput } from './ColorInput';
import { ImageCropper } from './ImageCropper';
import { ObjectsStack } from './ObjectsStack';
import { intToHex, hexToInt } from '../utils';

interface PropertiesPanelProps {
    objects?: CanvasObject[];
    selectedObjects: string[];
    selectedObject: CanvasObject | null;
    onUpdateObjects: (updates: Partial<CanvasObject>) => void;
    onReorderObjects?: (newObjects: CanvasObject[]) => void;
    onSelectObject?: (objectId: string) => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
    objects = [],
    selectedObjects,
    selectedObject,
    onUpdateObjects,
    onReorderObjects,
    onSelectObject
}) => {
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('propertiesPanelCollapsed');
        return saved ? JSON.parse(saved) : false;
    });
    const [activeTab, setActiveTab] = useState<'properties' | 'layers'>('properties');

    const handleToggleCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        localStorage.setItem('propertiesPanelCollapsed', JSON.stringify(collapsed));
    };

    const propertyBoxStyle: React.CSSProperties = {
        border: '1px solid #e8e8e8',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '8px',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
    };

    const labelStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: '11px',
        color: '#595959',
        marginBottom: '8px',
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    if (isCollapsed) {
        return (
            <div className="properties-panel" style={{ width: '56px', padding: '16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'width 0.3s ease' }}>
                <Button
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={() => handleToggleCollapsed(false)}
                    style={{ marginBottom: '16px' }}
                />
                <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 600, color: '#595959', letterSpacing: '2px', fontSize: '12px' }}>
                    PROPERTIES & LAYERS
                </div>
            </div>
        );
    }

    return (
        <div className="properties-panel" style={{ transition: 'width 0.3s ease' }}>
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as 'properties' | 'layers')}
                size="small"
                tabBarExtraContent={
                <Button
                    type="text"
                    icon={<RightOutlined />}
                    onClick={() => handleToggleCollapsed(true)}
                    size="small"
                />
            }
            items={[
                { label: 'PROPERTIES', key: 'properties' },
                { label: 'LAYERS', key: 'layers' }
            ]}
            style={{ marginBottom: '16px' }}
        />

            {activeTab === 'layers' && (
                <ObjectsStack
                    objects={objects}
                    selectedObjects={selectedObjects}
                    onReorderObjects={onReorderObjects || (() => { })}
                    onSelectObject={onSelectObject || (() => { })}
                />
            )}

            {activeTab === 'properties' && selectedObjects.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#888', fontStyle: 'italic', fontSize: '12px' }}>
                    Select an object to edit properties
                </div>
            )}

            {activeTab === 'properties' && selectedObjects.length > 1 && (
                <div>
                    <div style={{
                        ...propertyBoxStyle,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '20px'
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}>{selectedObjects.length} OBJECTS SELECTED</p>
                    </div>
                </div>
            )}

            {activeTab === 'properties' && selectedObject && (
                <div>
                    <div className="property-group" style={propertyBoxStyle}>
                        <label style={labelStyle}>📍 Position</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Input
                                size="small"
                                addonBefore="X"
                                value={Math.round(selectedObject.position.x)}
                                onChange={(e) => onUpdateObjects({ position: { ...selectedObject.position, x: Number(e.target.value) || 0 } })}
                            />
                            <Input
                                size="small"
                                addonBefore="Y"
                                value={Math.round(selectedObject.position.y)}
                                onChange={(e) => onUpdateObjects({ position: { ...selectedObject.position, y: Number(e.target.value) || 0 } })}
                            />
                        </div>
                    </div>

                    <div className="property-group" style={propertyBoxStyle}>
                        <label style={labelStyle}>👁️ Opacity & Layer</label>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: '10px', color: '#888' }}>Opacity ({Math.round((selectedObject.opacity ?? 1) * 100)}%)</span>
                            <Slider
                                min={0}
                                max={1}
                                step={0.1}
                                value={selectedObject.opacity ?? 1}
                                onChange={(value) => onUpdateObjects({ opacity: value })}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '10px', color: '#888', width: 60 }}>Z-Index:</span>
                            <Input
                                size="small"
                                type="number"
                                value={selectedObject.zIndex ?? 0}
                                onChange={(e) => onUpdateObjects({ zIndex: parseInt(e.target.value) || 0 })}
                            />
                        </div>
                    </div>

                    {selectedObject.type !== 'arrow' && (
                        <div className="property-group" style={propertyBoxStyle}>
                            <label style={labelStyle}>📏 Size</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Input
                                    size="small"
                                    addonBefore="W"
                                    value={Math.round(selectedObject.size.width)}
                                    onChange={(e) => onUpdateObjects({ size: { ...selectedObject.size, width: Number(e.target.value) || 1 } })}
                                />
                                <Input
                                    size="small"
                                    addonBefore="H"
                                    value={Math.round(selectedObject.size.height)}
                                    onChange={(e) => onUpdateObjects({ size: { ...selectedObject.size, height: Number(e.target.value) || 1 } })}
                                />
                            </div>
                        </div>
                    )}

                    {selectedObject.type !== 'arrow' && selectedObject.type !== 'circle' && (
                        <div className="property-group" style={propertyBoxStyle}>
                            <label style={labelStyle}>🔄 Rotation (°)</label>
                            <Slider
                                min={0}
                                max={360}
                                value={selectedObject.rotation || 0}
                                onChange={(value) => onUpdateObjects({ rotation: value })}
                                marks={{ 0: '0°', 90: '90°', 180: '180°', 270: '270°', 360: '360°' }}
                            />
                        </div>
                    )}

                    {/* Common Color Property for Shape/Text mainly. Arrow uses it too. Image maybe border color? */}
                    {/* MD: BaseCanvasObject has 'color'. */}
                    {'color' in selectedObject && (
                        <div className="property-group" style={propertyBoxStyle}>
                            <label className="property-label" style={{ fontWeight: 700 }}>🎨 COLOR</label>
                            <ColorInput
                                value={intToHex(selectedObject.color)}
                                onChange={(hex) => onUpdateObjects({ color: hexToInt(hex) } as any)}
                            />
                        </div>
                    )}

                    {selectedObject.type === 'text' && (
                        <>
                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>✏️ TEXT CONTENT</label>
                                <Input.TextArea
                                    value={selectedObject.text}
                                    onChange={(e) => onUpdateObjects({ text: e.target.value })}
                                    rows={2}
                                    placeholder="Enter your text here..."
                                    style={{ fontWeight: selectedObject.fontWeight === 6 ? 'bold' : 'normal' }}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>🔤 FONT SIZE ({selectedObject.fontSize}px)</label>
                                <Slider
                                    className="radius-slider"
                                    min={8}
                                    max={72}
                                    value={selectedObject.fontSize}
                                    onChange={(value) => onUpdateObjects({ fontSize: value })}
                                    marks={{ 8: '8px', 24: '24px', 48: '48px', 72: '72px' }}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>
                                    🔤 FONT FAMILY
                                </label>
                                <Select
                                    value={selectedObject.fontFamily || 'Roboto'}
                                    onChange={(value) => onUpdateObjects({ fontFamily: value } as any)}
                                    style={{ width: '100%', fontFamily: selectedObject.fontFamily ? `'${selectedObject.fontFamily}', sans-serif` : 'Roboto, sans-serif' }}
                                >
                                    <Select.Option value="Roboto" style={{ fontFamily: "'Roboto', sans-serif" }}>Roboto</Select.Option>
                                    <Select.Option value="DancingScript" style={{ fontFamily: "'DancingScript', cursive" }}>Dancing Script</Select.Option>
                                    <Select.Option value="JosefinSans" style={{ fontFamily: "'JosefinSans', sans-serif" }}>Josefin Sans</Select.Option>
                                    <Select.Option value="Inconsolata" style={{ fontFamily: "'Inconsolata', monospace" }}>Inconsolata</Select.Option>
                                    <Select.Option value="Oswald" style={{ fontFamily: "'Oswald', sans-serif" }}>Oswald</Select.Option>
                                    <Select.Option value="Montserrat" style={{ fontFamily: "'Montserrat', sans-serif" }}>Montserrat</Select.Option>
                                    <Select.Option value="Pacifico" style={{ fontFamily: "'Pacifico', cursive" }}>Pacifico</Select.Option>
                                    <Select.Option value="ArchivoBlack" style={{ fontFamily: "'ArchivoBlack', sans-serif" }}>Archivo Black</Select.Option>
                                    <Select.Option value="BebasNeue" style={{ fontFamily: "'BebasNeue', sans-serif" }}>Bebas Neue</Select.Option>
                                </Select>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>
                                    💪 FONT WEIGHT
                                </label>

                                <Select
                                    value={String(selectedObject.fontWeight ?? 3)} // Default Regular(3)
                                    onChange={(value) => onUpdateObjects({ fontWeight: Number(value) } as any)}
                                    style={{ width: '100%', fontWeight: 600 }}
                                >
                                    <Select.Option value="0">Thin (100)</Select.Option>
                                    <Select.Option value="1">Extra Light (200)</Select.Option>
                                    <Select.Option value="2">Light (300)</Select.Option>
                                    <Select.Option value="3">Regular (400)</Select.Option>
                                    <Select.Option value="4">Medium (500)</Select.Option>
                                    <Select.Option value="5">Semi Bold (600)</Select.Option>
                                    <Select.Option value="6">Bold (700)</Select.Option>
                                    <Select.Option value="7">Extra Bold (800)</Select.Option>
                                    <Select.Option value="8">Black (900)</Select.Option>
                                </Select>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>ALIGNMENT</label>
                                <Select
                                    value={selectedObject.textAlign || 0}
                                    onChange={(value) => onUpdateObjects({ textAlign: value } as any)}
                                    style={{ width: '100%' }}
                                >
                                    <Select.Option value={0}>Left</Select.Option>
                                    <Select.Option value={1}>Right</Select.Option>
                                    <Select.Option value={2}>Center</Select.Option>
                                    <Select.Option value={3}>Justify</Select.Option>
                                </Select>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>STYLE</label>
                                <Button.Group style={{ width: '100%' }}>
                                    <Button
                                        type={selectedObject.isBold ? 'primary' : 'default'}
                                        onClick={() => onUpdateObjects({ isBold: !selectedObject.isBold } as any)}
                                        style={{ width: '33%' }}
                                    >B</Button>
                                    <Button
                                        type={selectedObject.isItalic ? 'primary' : 'default'}
                                        onClick={() => onUpdateObjects({ isItalic: !selectedObject.isItalic } as any)}
                                        style={{ width: '33%', fontStyle: 'italic' }}
                                    >I</Button>
                                    <Button
                                        type={selectedObject.isUnderlined ? 'primary' : 'default'}
                                        onClick={() => onUpdateObjects({ isUnderlined: !selectedObject.isUnderlined } as any)}
                                        style={{ width: '33%', textDecoration: 'underline' }}
                                    >U</Button>
                                </Button.Group>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER COLOR</label>
                                <ColorInput
                                    value={intToHex(selectedObject.borderColor)}
                                    onChange={(color) => onUpdateObjects({ borderColor: hexToInt(color) } as any)}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER WIDTH ({selectedObject.borderWidth || 0}px)</label>
                                <Slider
                                    className="radius-slider"
                                    min={0}
                                    max={10}
                                    value={selectedObject.borderWidth || 0}
                                    onChange={(value) => onUpdateObjects({ borderWidth: value } as any)}
                                    marks={{ 0: '0px', 2: '2px', 5: '5px', 10: '10px' }}
                                />
                            </div>
                        </>
                    )}

                    {selectedObject.type === 'image' && (
                        <>
                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>🖼️ IMAGE DETAILS</label>
                                <Input
                                    size="small"
                                    placeholder="Image Name"
                                    value={selectedObject.imageName || ''}
                                    onChange={(e) => onUpdateObjects({ imageName: e.target.value } as any)}
                                    style={{ marginBottom: 8 }}
                                />
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={(file) => {
                                        const reader = new FileReader();
                                        reader.onload = (e) => {
                                            if (e.target?.result) {
                                                onUpdateObjects({
                                                    imageUrl: e.target.result as string,
                                                    imagePath: file.name // Map to imageName/imagePath
                                                } as any);
                                            }
                                        };
                                        reader.readAsDataURL(file);
                                        return false;
                                    }}
                                >
                                    <Button icon={<UploadOutlined />} style={{ width: '100%', fontWeight: 600 }}>
                                        {selectedObject.imageUrl ? 'CHANGE IMAGE' : 'UPLOAD IMAGE'}
                                    </Button>
                                </Upload>
                            </div>


                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER COLOR</label>
                                <ColorInput
                                    value={intToHex(selectedObject.borderColor)}
                                    onChange={(color) => onUpdateObjects({ borderColor: hexToInt(color) } as any)}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER WIDTH ({selectedObject.borderWidth || 0}px)</label>
                                <Slider
                                    className="radius-slider"
                                    min={0}
                                    max={10}
                                    value={selectedObject.borderWidth || 0}
                                    onChange={(value) => onUpdateObjects({ borderWidth: value } as any)}
                                    marks={{ 0: '0px', 2: '2px', 5: '5px', 10: '10px' }}
                                />
                            </div>
                        </>
                    )}

                    {selectedObject.type === 'arrow' && (
                        <>
                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>🔀 ARROW DETAILS</label>
                                <div style={{ marginBottom: 8 }}>
                                    <span style={{ fontSize: '10px', color: '#888' }}>Type</span>
                                    <Select
                                        value={selectedObject.arrowType || 'single'}
                                        onChange={(value) => onUpdateObjects({ arrowType: value } as any)}
                                        style={{ width: '100%', fontWeight: 600 }}
                                    >
                                        <Select.Option value="single">Single ➝</Select.Option>
                                        <Select.Option value="double">Double ↔</Select.Option>
                                    </Select>
                                </div>
                                <div>
                                    <span style={{ fontSize: '10px', color: '#888' }}>Line Style</span>
                                    <Select
                                        value={selectedObject.lineStyle || 'solid'}
                                        onChange={(value) => onUpdateObjects({ lineStyle: value } as any)}
                                        style={{ width: '100%', fontWeight: 600 }}
                                    >
                                        <Select.Option value="solid">Solid ━━━</Select.Option>
                                        <Select.Option value="dashed">Dashed ╌╌╌</Select.Option>
                                        <Select.Option value="dotted">Dotted ...</Select.Option>
                                    </Select>
                                </div>
                                <div style={{ marginTop: 8 }}>
                                    <span style={{ fontSize: '10px', color: '#888' }}>Arrow Style</span>
                                    <Select
                                        value={selectedObject.arrowStyle || 'straight'}
                                        onChange={(value) => onUpdateObjects({ arrowStyle: value } as any)}
                                        style={{ width: '100%', fontWeight: 600 }}
                                    >
                                        <Select.Option value="straight">Straight</Select.Option>
                                        <Select.Option value="curve">Curve</Select.Option>
                                        <Select.Option value="elbow">Elbow</Select.Option>
                                    </Select>
                                </div>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>HEAD CONFIG</label>
                                <div style={{ marginBottom: 8 }}>
                                    <span style={{ fontSize: '10px', color: '#888' }}>Size ({selectedObject.arrowHeadSize ?? 1}x)</span>
                                    <Slider
                                        min={0.5}
                                        max={3}
                                        step={0.1}
                                        value={selectedObject.arrowHeadSize ?? 1}
                                        onChange={(value) => onUpdateObjects({ arrowHeadSize: value } as any)}
                                    />
                                </div>
                                <div>
                                    <span style={{ fontSize: '10px', color: '#888' }}>Head Color</span>
                                    <ColorInput
                                        value={selectedObject.arrowHeadColor ? intToHex(selectedObject.arrowHeadColor) : intToHex(selectedObject.color)}
                                        onChange={(hex) => onUpdateObjects({ arrowHeadColor: hexToInt(hex) } as any)}
                                    />
                                </div>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <Checkbox
                                    checked={selectedObject.hasBreakpoint}
                                    onChange={(e) => onUpdateObjects({ hasBreakpoint: e.target.checked } as any)}
                                >
                                    Enable Breakpoint
                                </Checkbox>
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>
                                    📏 STROKE WIDTH ({selectedObject.strokeWidth || 2}px)
                                </label>
                                <Slider
                                    min={1}
                                    max={20}
                                    value={selectedObject.strokeWidth || 2}
                                    onChange={(value) => onUpdateObjects({ strokeWidth: value } as any)}
                                    marks={{ 1: '1', 5: '5', 10: '10', 15: '15', 20: '20' }}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>🎯 END POINT</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Input
                                        size="small"
                                        addonBefore="X"
                                        value={Math.round(selectedObject.endPosition?.x ?? 100)}
                                        onChange={(e) => onUpdateObjects({ endPosition: { ...selectedObject.endPosition, x: Number(e.target.value) || 0 } } as any)}
                                        style={{ fontWeight: 600 }}
                                    />
                                    <Input
                                        size="small"
                                        addonBefore="Y"
                                        value={Math.round(selectedObject.endPosition?.y ?? 0)}
                                        onChange={(e) => onUpdateObjects({ endPosition: { ...selectedObject.endPosition, y: Number(e.target.value) || 0 } } as any)}
                                        style={{ fontWeight: 600 }}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {(selectedObject.type === 'rectangle' || selectedObject.type === 'circle' || selectedObject.type === 'triangle' || selectedObject.type === 'diamond' || selectedObject.type === 'hexagon' || selectedObject.type === 'star') && (
                        <>
                            {/* Shape Color (Fill) already handled in common Color property */}

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER COLOR</label>
                                <ColorInput
                                    value={intToHex(selectedObject.borderColor)}
                                    onChange={(color) => onUpdateObjects({ borderColor: hexToInt(color) } as any)}
                                />
                            </div>

                            <div className="property-group" style={propertyBoxStyle}>
                                <label className="property-label" style={{ fontWeight: 700 }}>BORDER WIDTH ({selectedObject.borderWidth || 0}px)</label>
                                <Slider
                                    className="radius-slider"
                                    min={0}
                                    max={10}
                                    value={selectedObject.borderWidth || 0}
                                    onChange={(value) => onUpdateObjects({ borderWidth: value } as any)}
                                    marks={{ 0: '0px', 2: '2px', 5: '5px', 10: '10px' }}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};